import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags';
import { AppState } from '../../types/store';
import {
  SectionLayout,
  SectionLayoutZones,
  SectionLayoutZone,
} from '../../types/layout';
import { getModuleType } from './modules';

export const getSectionLayout = (state: AppState, section): SectionLayout =>
  state.layout[section] || {};

export const getSectionLayoutZones = (
  state: AppState,
  section
): SectionLayoutZones => getSectionLayout(state, section).zones || {};

export const getSectionLayoutVersion = (state: AppState, section): number =>
  getSectionLayout(state, section).version || 0;

export const getSectionLayoutZone = (
  state: AppState,
  section,
  zone
): SectionLayoutZone => getSectionLayoutZones(state, section)[zone] || {};

export const getAllInstalledModuleIds = (state: AppState, section) => {
  const zones = Object.keys(getSectionLayout(state, section).zones || {});
  return zones.reduce(
    (acc, zone) => {
      acc = acc.concat(getInstalledModuleIds(state, section, zone));
      return acc;
    },
    [] as number[]
  );
};
export const getInstalledModuleIds = (state: AppState, section, zone) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  if (!zoneLayout) {
    return [];
  }
  return zoneLayout.installedModulesIds || [];
};

export const getInstalledModuleTypes = (state: AppState, section, zone) =>
  getInstalledModuleIds(state, section, zone).map(moduleId =>
    getModuleType(state, moduleId)
  );

export const getInstallableModuleInfo = (state: AppState, section, zone) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  if (!zoneLayout) {
    return [];
  }

  const installedModuleTypes = getInstalledModuleTypes(state, section, zone);
  const installedModuleCounts = {};
  installedModuleTypes.forEach(moduleType => {
    if (installedModuleCounts[moduleType]) {
      installedModuleCounts[moduleType]++;
    } else {
      installedModuleCounts[moduleType] = 1;
    }
  });
  const isEclipseCoreCustomBoxFeatureEnabled = flagEnabled(
    state,
    'eclipse_core_custombox'
  );
  const installableModules = (zoneLayout.installableModuleTypes || [])
    .map(moduleInfo => ({
      ...moduleInfo,
      installedCount: installedModuleCounts[moduleInfo.moduleName] || 0,
    }))
    .filter(({ installedCount, limit, restrict }) => {
      const coreLimit =
        isEclipseCoreCustomBoxFeatureEnabled &&
        restrict &&
        restrict.priv === 'core'
          ? restrict.limit
          : 0;
      return installedCount < Math.max(limit, coreLimit);
    });

  return installableModules;
};

export const getInstallableModuleTypes = (state: AppState, section, zone) =>
  getInstallableModuleInfo(state, section, zone).map(
    moduleInfo => moduleInfo.moduleName
  );

export const getIsInstallingModule = (state: AppState, section, zone) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  return zoneLayout.isInstallingModule;
};

export const getCurrentlyInstalledModulePostion = (
  state: AppState,
  section,
  zone
) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  return zoneLayout.currentlyInstalledModulePostion;
};
